import axios from 'axios';

// JWT Service for managing authentication tokens.
// NOTE: This service handles token STORAGE and UX-level expiry checks only.
// Cryptographic signature verification is performed exclusively by the backend
// (JWTMiddleware in mme-tagging-service). The frontend cannot and must not attempt
// to verify signatures — the signing secret must never appear in browser code.
export class JWTService {
  constructor() {
    this.verifierBaseUrl = this.getJWTVerifierUrl();
    // secretKey intentionally absent — signing secrets do not belong in browser JS.
  }

  getJWTVerifierUrl() {
    const currentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      return 'docker://mme-jwt-verifier';
    }

    // Production: use GCP backend
    return process.env.REACT_APP_TAGGING_API_URL || 'https://api.railtech.io';
  }

  async generateJWT(payload) {
    // Token generation always goes through the server — the browser never signs tokens.
    // In development the server is reached via the CRA proxy (/api/auth/generate-jwt).
    // In production Traefik routes the same path to the jwt-verifier sidecar.
    const jwtPayload = {
      userId: payload.userId || 'demo-user',
      role: payload.role || 'user',
      org_id: payload.orgId || payload.org_id || 'demo-org',
      name: payload.name || 'Demo User',
      email: payload.email || 'demo@example.com',
    };

    try {
      if (!this.verifierBaseUrl.startsWith('docker://')) {
        // Production: call the real JWT verifier service directly
        const response = await axios.post(`${this.verifierBaseUrl}/generate`, jwtPayload);
        return response.data.token;
      }
      // Development (docker): call through the CRA proxy so the browser never
      // needs a direct route to the jwt-verifier container.
      return await this.getProductionJWT(jwtPayload);
    } catch (error) {
      console.error('JWT generation failed:', error);
      throw new Error('Failed to generate authentication token — ensure the auth service is reachable.');
    }
  }

  async validateJWT(token) {
    // ⚠️  SECURITY NOTE: This method is a UX convenience ONLY.
    // It checks whether the token is structurally valid and not yet expired so
    // the UI can show "session expired" before sending a doomed request.
    // It does NOT verify the cryptographic signature — that is performed by
    // JWTMiddleware on the backend (mme-tagging-service/internal/http/middleware/jwt.go).
    // Never use the return value of this function to make an access-control decision.
    if (!token || typeof token !== 'string') {
      return { expired: true, error: 'Missing token' };
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return { expired: true, error: 'Invalid JWT structure' };
      }

      const payload = JSON.parse(atob(parts[1]));

      if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
        return { expired: true, error: 'Token expired', expiresAt: new Date(payload.exp * 1000) };
      }

      const userId = payload.userId || payload.sub;
      const role = payload.role || (payload.roles && payload.roles[0]) || 'user';
      const orgId = payload.org_id || payload.orgId;

      if (!userId || !orgId) {
        return { expired: true, error: 'Token missing required fields' };
      }

      // Normalize the payload so callers see a consistent shape
      const normalized = { ...payload, userId, role, org_id: orgId };

      return {
        valid: true,
        expired: false,
        payload: normalized,
        expiresAt: payload.exp ? new Date(payload.exp * 1000) : null,
      };
    } catch (error) {
      return { expired: true, error: error.message };
    }
  }

  async refreshToken(currentToken) {
    try {
      const validation = await this.validateJWT(currentToken);
      if (!validation.valid) {
        throw new Error('Cannot refresh invalid token');
      }

      // Generate new token with same payload but updated timestamps
      const newPayload = {
        ...validation.payload,
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours
      };

      return await this.generateJWT(newPayload);
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw new Error('Failed to refresh token');
    }
  }

  getUserFromToken(token) {
    try {
      // Parse JWT payload directly (synchronous version for immediate use)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid JWT structure');
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));
      
      const userId = payload.userId || payload.sub;
      const role = payload.role || (payload.roles && payload.roles[0]) || 'user';
      const orgId = payload.org_id || payload.orgId;

      if (!userId || !orgId) {
        console.error('Invalid token payload - missing required fields');
        return null;
      }

      if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
        console.error('Token is expired');
        return null;
      }

      return {
        id: userId,
        userId,
        name: payload.name || 'User',
        email: payload.email || '',
        role: role.toLowerCase(),
        orgId,
        expiresAt: payload.exp ? new Date(payload.exp * 1000) : null
      };
    } catch (error) {
      console.error('Failed to extract user from token:', error);
      return null;
    }
  }

  isTokenExpired(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true;
      }

      const payload = JSON.parse(atob(parts[1]));
      
      // Check expiration
      if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
        return true;
      }

      return false;
    } catch {
      return true;
    }
  }

  getTokenExpirationTime(token) {
    try {
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1]));
      return payload.exp ? new Date(payload.exp * 1000) : null;
    } catch {
      return null;
    }
  }

  // getDemoTokens() removed — client-side token generation was cryptographically
  // unsound (unsigned). Quick-login flows now call generateJWT() which routes
  // through the server. See LoginPage.js handleQuickLogin for usage.

  // Get production-grade JWT from the actual JWT container
  async getProductionJWT(payload) {
    try {
      // This would call the actual JWT verifier service
      // For now, simulate with proper format
      const response = await fetch('/api/auth/generate-jwt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: payload.userId,
          role: payload.role,
          org_id: payload.orgId,
          name: payload.name,
          email: payload.email
        })
      });

      if (!response.ok) {
        throw new Error('JWT generation failed');
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      // Do NOT fall back to client-side token generation — the signing secret
      // must never appear in browser code.  Re-throw so the caller surfaces
      // a clear "auth service unreachable" error to the user.
      console.error('JWT generation failed — auth service unreachable:', error);
      throw new Error('Authentication service is unavailable. Please try again later.');
    }
  }
}

// Create singleton instance
export const jwtService = new JWTService();

export default jwtService;
